import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { api } from './api/api';
import { AuthContext } from '../App';

// ── Constants ─────────────────────────────────────────────────

const ALL_RACES = Array.from({ length: 17 }, (_, i) => i + 1);

const DRIVER_LEVELS = ['rookie', 'amateur', 'pro', 'master', 'elite'];

const CATEGORIES = [
    { value: 'important',   label: 'Important',  color: '#b23b3b' },
    { value: 'medium',      label: 'Medium',     color: '#9a7d2e' },
    { value: 'unimportant', label: 'Unimportant',color: '#3b6b7a' },
];

const DRIVER_TRAININGS = [
    { value: '',                 label: '— (manual)'      },
    { value: 'fitnessClass',     label: 'Fitness Class'   },
    { value: 'yoga',             label: 'Yoga'            },
    { value: 'prTraining',       label: 'PR Training'     },
    { value: 'techTraining',     label: 'Tech Training'   },
    { value: 'sportsPsychology', label: 'Sports Psych.'   },
    { value: 'ninjaTraining',    label: 'Ninja Training'  },
];

const TESTING_PRIORITIES = ['', 'power', 'handling', 'acceleration', 'braking', 'overtaking', 'chicanes', 'balanced'];

const PARTS = [
    { key: 'chassis', label: 'Chassis' }, { key: 'engine', label: 'Engine' },
    { key: 'frontWing', label: 'Front Wing' }, { key: 'rearWing', label: 'Rear Wing' },
    { key: 'underbody', label: 'Underbody' }, { key: 'sidepods', label: 'Sidepods' },
    { key: 'cooling', label: 'Cooling' }, { key: 'gearbox', label: 'Gearbox' },
    { key: 'brakes', label: 'Brakes' }, { key: 'suspension', label: 'Suspension' },
    { key: 'electronics', label: 'Electronics' },
];

// driver training options (one per race, mutually exclusive incl. Spa)
const DRIVER_TRAIN_FIELDS = ['fitnessClass', 'yoga', 'prTraining', 'techTraining', 'sportsPsychology', 'ninjaTraining'];
const DRIVER_TRAIN_OPTS = [
    { value: '', label: '—' },
    { value: 'fitnessClass', label: 'Fitness' }, { value: 'yoga', label: 'Yoga' },
    { value: 'prTraining', label: 'PR' }, { value: 'techTraining', label: 'Tech' },
    { value: 'sportsPsychology', label: 'Sports Psy' }, { value: 'ninjaTraining', label: 'Ninja' },
    { value: 'spa', label: 'Spa (energy)' },
];

// Season Gains PHA colouring: top-ranked attribute magenta, 2nd blue.
// ascending=true → lowest value is "most important" (track offsets); false → highest (car).
function phaColors(p, h, a, ascending) {
    if (p == null || h == null || a == null) return {};
    const arr = [{ k: 'p', v: p }, { k: 'h', v: h }, { k: 'a', v: a }];
    arr.sort((x, y) => ascending ? x.v - y.v : y.v - x.v);
    return { [arr[0].k]: '#c026d3', [arr[1].k]: '#4472C4' };
}

// ── Helpers ───────────────────────────────────────────────────

function fmtMoney(val) {
    if (val === null || val === undefined) return '—';
    const abs = Math.abs(val);
    const sign = val < 0 ? '-' : '';
    if (abs >= 1_000_000) return sign + '$' + (abs / 1_000_000).toFixed(2).replace(/\.00$/, '') + 'M';
    if (abs >= 1_000) return sign + '$' + (abs / 1_000).toFixed(0) + 'k';
    return sign + '$' + abs;
}

export default function SeasonPlanner() {
    const { setApiRequests, userRole } = useContext(AuthContext);
    const isAdmin = userRole === 'ADMIN';

    const [season, setSeason] = useState(null);
    const [slot, setSlot] = useState(1);
    const [slots, setSlots] = useState([]);
    const [plan, setPlan] = useState(null);
    const [racesMap, setRacesMap] = useState({});
    const [settings, setSettings] = useState(null);
    const [draft, setDraft] = useState(null);       // local editable copy of settings (Submit to apply)
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [status, setStatus] = useState(null); // 'saving' | 'saved' | 'optimizing' | 'failed'
    const [manualBalance, setManualBalance] = useState('');
    const [adminUserId, setAdminUserId] = useState('');   // admin: view another user's plans

    const debounceRef = useRef({});

    const userQS = useCallback((extra) => {
        const p = new URLSearchParams();
        if (isAdmin && adminUserId) p.set('userId', adminUserId);
        if (extra) Object.entries(extra).forEach(([k, v]) => { if (v != null) p.set(k, v); });
        const s = p.toString();
        return s ? `?${s}` : '';
    }, [isAdmin, adminUserId]);

    const applyPlan = useCallback((data) => {
        setPlan(data);
        const map = {};
        (data.races || []).forEach(r => { map[r.raceNumber] = r; });
        setRacesMap(map);
        setSettings(data.settings || {});
        setDraft(data.settings || {});
        setManualBalance(
            data.manualStartingBalance != null ? String(data.manualStartingBalance) : ''
        );
        if (data.tokensRemaining != null) setApiRequests(data.tokensRemaining);
    }, [setApiRequests]);

    // season from /update/today
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await api.get('/update/today');
                if (cancelled) return;
                if (res.data?.apiRequestsRemaining != null) setApiRequests(res.data.apiRequestsRemaining);
                setSeason(res.data?.season ?? 113);
            } catch {
                if (!cancelled) setSeason(113);
            }
        })();
        return () => { cancelled = true; };
    }, [setApiRequests]);

    const loadSlots = useCallback(async () => {
        if (season == null) return;
        try {
            const res = await api.get(`/season-plan/${season}/plans${userQS()}`);
            setSlots(res.data || []);
        } catch { /* ignore */ }
    }, [season, userQS]);

    const loadPlan = useCallback(async () => {
        if (season == null) return;
        setLoading(true);
        setError(null);
        try {
            const res = await api.get(`/season-plan/${season}/${slot}${userQS()}`);
            applyPlan(res.data);
        } catch {
            setError('Failed to load season plan.');
        } finally {
            setLoading(false);
        }
    }, [season, slot, userQS, applyPlan]);

    useEffect(() => { loadSlots(); }, [loadSlots]);
    useEffect(() => { loadPlan(); }, [loadPlan]);

    // ── Actions ───────────────────────────────────────────────

    const currentRace = plan?.currentRaceNumber || 1;

    const runOptimize = useCallback(async (fromRace) => {
        setStatus('optimizing');
        try {
            const res = await api.post(`/season-plan/${season}/${slot}/optimize${userQS({ fromRace })}`);
            applyPlan(res.data);
            setStatus('saved');
            loadSlots();
        } catch {
            setStatus('failed');
        }
    }, [season, slot, userQS, applyPlan, loadSlots]);

    const saveSettings = useCallback(async (next) => {
        setSettings(next);
        setStatus('saving');
        try {
            const res = await api.patch(`/season-plan/${season}/${slot}/settings${userQS()}`, next);
            applyPlan(res.data);
            setStatus('saved');
        } catch {
            setStatus('failed');
        }
    }, [season, slot, userQS, applyPlan]);

    // Edit the local draft only; nothing hits the server until "Apply settings".
    const setField = (key, value) => setDraft(prev => ({ ...(prev || {}), [key]: value }));
    const settingsDirty = JSON.stringify(draft) !== JSON.stringify(settings);

    const bulkCategory = useCallback(async (value) => {
        setStatus('saving');
        try {
            const res = await api.patch(`/season-plan/${season}/${slot}/bulk-category${userQS({ value })}`);
            applyPlan(res.data);
            setStatus('saved');
        } catch { setStatus('failed'); }
    }, [season, slot, userQS, applyPlan]);

    const patchRace = useCallback((raceNumber, field, value) => {
        setRacesMap(prev => ({
            ...prev,
            [raceNumber]: { ...(prev[raceNumber] || { raceNumber }), [field]: value },
        }));
        const ref = debounceRef.current;
        if (!ref[raceNumber]) ref[raceNumber] = { timer: null, pending: {} };
        ref[raceNumber].pending[field] = value;
        if (ref[raceNumber].timer) clearTimeout(ref[raceNumber].timer);
        ref[raceNumber].timer = setTimeout(async () => {
            const payload = ref[raceNumber].pending;
            ref[raceNumber].pending = {};
            ref[raceNumber].timer = null;
            setStatus('saving');
            try {
                const res = await api.patch(`/season-plan/${season}/${slot}/race/${raceNumber}${userQS()}`, payload);
                applyPlan(res.data);
                setStatus('saved');
            } catch {
                setStatus('failed');
            }
        }, 500);
    }, [season, slot, userQS, applyPlan]);

    const toggleLock = useCallback((raceNumber, field) => {
        const race = racesMap[raceNumber] || {};
        const locks = new Set(race.locks || []);
        if (locks.has(field)) locks.delete(field); else locks.add(field);
        patchRace(raceNumber, 'locks', Array.from(locks));
    }, [racesMap, patchRace]);

    const applyBalance = useCallback(async () => {
        const val = manualBalance.trim() === '' ? null : Number(manualBalance.replace(/[^0-9-]/g, ''));
        setStatus('saving');
        try {
            const res = await api.patch(`/season-plan/${season}/${slot}/starting-balance${userQS()}`, val, {
                headers: { 'Content-Type': 'application/json' },
            });
            applyPlan(res.data);
            setStatus('saved');
        } catch { setStatus('failed'); }
    }, [season, slot, userQS, manualBalance, applyPlan]);

    const setDriverLevel = useCallback(async (level) => {
        setStatus('saving');
        try {
            const res = await api.patch(`/season-plan/${season}/${slot}/driver-level${userQS()}`, level, {
                headers: { 'Content-Type': 'application/json' },
            });
            applyPlan(res.data);
            setStatus('saved');
        } catch { setStatus('failed'); }
    }, [season, slot, userQS, applyPlan]);

    const renameSlot = useCallback(async (s) => {
        const name = window.prompt('Plan name:', (slots.find(x => x.slot === s)?.name) || `Plan ${s}`);
        if (name == null) return;
        try {
            await api.patch(`/season-plan/${season}/${s}/name${userQS()}`, name, {
                headers: { 'Content-Type': 'application/json' },
            });
            loadSlots();
            if (s === slot) loadPlan();
        } catch { /* ignore */ }
    }, [season, slot, slots, userQS, loadSlots, loadPlan]);

    // ── Derived ───────────────────────────────────────────────

    const startBalance = plan?.effectiveStartingBalance ?? 0;
    const balBefore = {}, balAfter = {};
    let running = startBalance;
    ALL_RACES.forEach(n => {
        const r = racesMap[n] || {};
        balBefore[n] = running;
        running += (r.racePnl ?? 0);
        balAfter[n] = running;
    });

    const cal = plan?.calendar || [];
    const trackOf = (n) => {
        const c = cal.find(x => x.raceNumber === n);
        return c || {};
    };
    const trackLabel = (n) => {
        const c = trackOf(n);
        return c.trackName || c.trackNatCode || `R${n}`;
    };

    // Driver training is one-per-race (mutually exclusive incl. Spa): set all flags
    // via the debounced patchRace (it accumulates them into a single PATCH).
    const setTraining = (n, value) => {
        patchRace(n, 'spa', value === 'spa');
        DRIVER_TRAIN_FIELDS.forEach(f => patchRace(n, f, f === value));
    };
    const currentTraining = (n) => {
        const r = racesMap[n] || {};
        if (r.spa) return 'spa';
        return DRIVER_TRAIN_FIELDS.find(f => r[f]) || '';
    };

    const cp = plan?.carPha || {};
    const partsBase = {
        p: cp.partsPower ?? ((cp.power != null && cp.testingPower != null) ? cp.power - cp.testingPower : null),
        h: cp.partsHandling ?? ((cp.handling != null && cp.testingHandling != null) ? cp.handling - cp.testingHandling : null),
        a: cp.partsAcceleration ?? ((cp.acceleration != null && cp.testingAcceleration != null) ? cp.acceleration - cp.testingAcceleration : null),
    };
    const carAt = (n) => {
        const r = racesMap[n] || {};
        if (partsBase.p == null) return null;
        return {
            p: partsBase.p + (r.projectedTestPower ?? 0),
            h: partsBase.h + (r.projectedTestHandling ?? 0),
            a: partsBase.a + (r.projectedTestAcceleration ?? 0),
        };
    };

    // ── Render ────────────────────────────────────────────────

    if (loading && !plan) return <div className="fb-page-header"><div className="fb-page-title">Season Planner</div><div className="fb-page-sub">Loading…</div></div>;
    if (error) return <div className="fb-alert fb-alert-error" style={{ margin: 16 }}>{error} <button className="fb-btn fb-btn-sm" onClick={loadPlan}>Retry</button></div>;

    const d = draft || {};
    const infeasible = plan?.infeasibleRaces || [];

    // editable cell helpers
    const numCell = (n, field, min, max) => {
        const r = racesMap[n] || {};
        return <input type="number" min={min} max={max} value={r[field] ?? ''}
            style={{ width: 46 }}
            onChange={e => patchRace(n, field, e.target.value === '' ? null : Number(e.target.value))} />;
    };
    const lockTog = (n, field) => {
        const r = racesMap[n] || {};
        const on = (r.locks || []).includes(field);
        return <span onClick={() => toggleLock(n, field)} title="lock / unlock" style={{ cursor: 'pointer', marginLeft: 3, opacity: on ? 1 : 0.3 }}>{on ? '🔒' : '🔓'}</span>;
    };

    // row group builders
    const staffTrainings = (n) => {
        const r = racesMap[n] || {};
        const t = [];
        if (r.stressHandling) t.push('Stress');
        if (r.concentration) t.push('Conc');
        if (r.efficiency) t.push('Eff');
        return t.length ? t.join(', ') : '—';
    };
    const plannedParts = (n) => {
        const r = racesMap[n] || {};
        const parts = r.parts || {};
        const keys = Object.keys(parts).filter(k => parts[k]);
        if (!keys.length) return '—';
        const sym = (a) => a === 'upgrade' ? '↑' : a === 'replace' ? '↻' : a === 'downgrade' ? '↓' : '·';
        return keys.map(k => `${k}${sym(parts[k].action)}${parts[k].toLevel ?? ''}`).join(', ');
    };

    const sections = [
        { title: 'Schedule', rows: [
            { label: 'Track', render: n => <b>{trackLabel(n)}</b> },
            { label: 'Category', render: n => {
                const r = racesMap[n] || {};
                return <span>
                    <select value={r.category || ''} onChange={e => patchRace(n, 'category', e.target.value || null)}
                        style={catStyle(r.category)}>
                        <option value="">auto</option>
                        {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>{lockTog(n, 'category')}
                </span>;
            } },
        ] },
        { title: 'Risk & Energy', rows: [
            { label: 'CT (clear risk)', render: n => <span>{numCell(n, 'ctRisk', 0, 100)}{lockTog(n, 'ctRisk')}</span> },
            { label: 'Energy start', render: n => { const r = racesMap[n] || {}; return <span style={{ color: eColor(r.projectedEnergyStart) }}>{fmt1(r.projectedEnergyStart)}</span>; } },
            { label: 'Energy end', render: n => { const r = racesMap[n] || {}; return <span style={{ color: eColor(r.projectedEnergyEnd) }}>{fmt1(r.projectedEnergyEnd)}{r.energyInfeasible ? ' ⚠' : ''}</span>; } },
            { label: 'Rain (talent)', render: n => { const r = racesMap[n] || {}; return <input type="checkbox" checked={!!r.rainExpected} onChange={e => patchRace(n, 'rainExpected', e.target.checked)} />; } },
        ] },
        { title: 'Balance', rows: [
            { label: 'Balance BEFORE', render: n => <span style={{ color: balBefore[n] < 0 ? 'var(--c-error)' : 'var(--c-text)' }}>{fmtMoney(balBefore[n])}</span> },
            { label: 'Balance AFTER', render: n => <strong style={{ color: balAfter[n] < 0 ? 'var(--c-error)' : 'var(--c-success)' }}>{fmtMoney(balAfter[n])}</strong> },
            { label: 'Race P&L', render: n => { const r = racesMap[n] || {}; return <span style={{ color: (r.racePnl ?? 0) < 0 ? 'var(--c-error)' : 'var(--c-text-2)' }}>{fmtMoney(r.racePnl)}</span>; } },
            { label: 'Race income', render: n => fmtMoney((racesMap[n] || {}).raceIncome) },
        ] },
        { title: 'Result', rows: [
            { label: 'Est. position', render: n => <span style={{ color: 'var(--c-text-muted)' }}>{(racesMap[n] || {}).estimatedPosition ?? '—'}</span> },
            { label: 'Race position', render: n => <span>{numCell(n, 'racePosition', 1, 40)}{lockTog(n, 'racePosition')}</span> },
            { label: 'Quali position', render: n => numCell(n, 'qualifyPosition', 1, 40) },
        ] },
        { title: 'Track PHA (importance, magenta = most)', rows: [
            { label: 'Track P / H / A %', render: n => { const r = racesMap[n] || {}; return coloredPha(r.trackPhaPower, r.trackPhaHandling, r.trackPhaAcceleration, false); } },
        ] },
        { title: 'Car PHA (parts + CCP, magenta = strongest)', rows: [
            { label: 'Car P / H / A', render: n => { const c = carAt(n); if (!c) return '—'; const col = phaColors(c.p, c.h, c.a, false); return <span><span style={{ color: col.p }}>{Math.round(c.p)}</span> / <span style={{ color: col.h }}>{Math.round(c.h)}</span> / <span style={{ color: col.a }}>{Math.round(c.a)}</span></span>; } },
            { label: 'Car P / H / A %', render: n => { const c = carAt(n); return c ? coloredPha(c.p, c.h, c.a, false) : '—'; } },
            { label: 'PHA match', render: n => {
                const r = racesMap[n] || {}; const c = carAt(n);
                const ts = shares(r.trackPhaPower, r.trackPhaHandling, r.trackPhaAcceleration);
                const cs = c ? shares(c.p, c.h, c.a) : null;
                if (!ts || !cs) return '—';
                const m = matchPct(ts, cs);
                const good = m >= 85;
                return <span style={{ color: good ? 'var(--c-success)' : (m >= 70 ? 'var(--c-warning)' : 'var(--c-error)'), fontWeight: 600 }}>{m}%</span>;
            } },
        ] },
        { title: 'Testing (CCP → car PHA)', rows: [
            { label: 'Priority', render: n => { const r = racesMap[n] || {}; return <span><select value={r.testingPriority || ''} onChange={e => patchRace(n, 'testingPriority', e.target.value || null)}>{TESTING_PRIORITIES.map(p => <option key={p} value={p}>{p || '—'}</option>)}</select>{lockTog(n, 'testingPriority')}</span>; } },
            { label: 'Test laps before', render: n => numCell(n, 'testLapsBefore', 0, 200) },
            { label: 'CCP P / H / A', render: n => { const r = racesMap[n] || {}; return (r.projectedTestPower != null) ? `${fmt1(r.projectedTestPower)} / ${fmt1(r.projectedTestHandling)} / ${fmt1(r.projectedTestAcceleration)}` : '—'; } },
            { label: 'CCP gain (ΣPHA)', render: n => {
                const r = racesMap[n] || {}; const prev = racesMap[n - 1];
                if (r.projectedTestPower == null) return '—';
                const cur = (r.projectedTestPower + r.projectedTestHandling + r.projectedTestAcceleration);
                const pv = prev && prev.projectedTestPower != null ? (prev.projectedTestPower + prev.projectedTestHandling + prev.projectedTestAcceleration) : cur;
                const g = cur - pv;
                return <span style={{ color: g > 0.05 ? 'var(--c-success)' : 'var(--c-text-muted)' }}>{g >= 0 ? '+' : ''}{fmt1(g)}</span>;
            } },
        ] },
        { title: 'Car parts (level · wear%, ↻ replace · ↑ upgrade)', rows: [
            { label: 'Max part wear %', render: n => { const r = racesMap[n] || {}; const w = r.maxPartWear; return w == null ? '—' : <span style={{ color: w >= 95 ? 'var(--c-error)' : (w >= 80 ? 'var(--c-warning)' : 'var(--c-success)'), fontWeight: w >= 80 ? 600 : 400 }}>{Math.round(w)}</span>; } },
            ...PARTS.map(part => ({ label: part.label, render: n => {
                const r = racesMap[n] || {};
                const lvl = (r.partLevel || {})[part.key];
                const w = (r.partWear || {})[part.key];
                const act = (r.parts || {})[part.key];
                const opts = (plan?.partOptions || {})[part.key] || [];
                const wc = w == null ? 'var(--c-text-muted)' : (w >= 95 ? 'var(--c-error)' : w >= 80 ? 'var(--c-warning)' : 'var(--c-text-2)');
                const sym = act ? (act.action === 'replace' ? '↻' : act.action === 'upgrade' ? '↑' : act.action === 'downgrade' ? '↓' : '') : '';
                const cur = lvl ?? act?.toLevel;
                const setPart = (val) => {
                    const next = { ...(r.parts || {}) };
                    if (val === '') delete next[part.key];
                    else {
                        const o = opts.find(x => String(x.toLevel) === val);
                        if (!o) return;
                        const action = cur == null ? 'replace' : (o.toLevel > cur ? 'upgrade' : o.toLevel === cur ? 'replace' : (o.cost === 0 ? 'downgrade' : 'replace'));
                        next[part.key] = { action, toLevel: o.toLevel, cost: o.cost };
                    }
                    patchRace(n, 'parts', next);
                };
                return <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                    L{lvl ?? '?'} <span style={{ color: wc, fontWeight: w >= 80 ? 600 : 400 }}>{w == null ? '' : w + '%'}</span>
                    <span style={{ color: 'var(--c-accent)' }}>{sym}{act?.toLevel ?? ''}</span>
                    {opts.length > 0 && <select value={act ? String(act.toLevel) : ''} onChange={e => setPart(e.target.value)} style={{ marginLeft: 3, fontSize: 10, maxWidth: 58 }} title="change part">
                        <option value="">—</option>
                        {opts.map(o => <option key={o.toLevel} value={o.toLevel}>L{o.toLevel} {o.cost === 0 ? 'free' : fmtMoney(o.cost)}</option>)}
                    </select>}
                </span>;
            } })),
        ] },
        { title: 'Training', rows: [
            { label: 'Driver training', render: n => <select value={currentTraining(n)} onChange={e => setTraining(n, e.target.value)}>{DRIVER_TRAIN_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select> },
            { label: 'Staff training', render: n => <span style={{ fontSize: 11 }}>{staffTrainings(n)}</span> },
        ] },
    ];

    return (
        <div style={{ padding: '18px 20px 40px' }}>
            <div className="fb-page-header" style={{ display: 'flex', alignItems: 'baseline', gap: 16, flexWrap: 'wrap' }}>
                <div className="fb-page-title">Season Planner</div>
                <div className="fb-page-sub">S{season} · GPRO tokens until next race: <b>{plan?.tokensRemaining ?? '—'}</b></div>
                {status && <span style={{ fontSize: 12, color: status === 'failed' ? 'var(--c-error)' : 'var(--c-success)' }}>
                    {status === 'optimizing' ? 'Optimizing…' : status === 'saving' ? 'Saving…' : status === 'failed' ? 'Failed' : 'Saved'}
                </span>}
            </div>

            {/* Slot tabs + admin target */}
            <div className="fb-row" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                {[1, 2, 3].map(s => {
                    const info = slots.find(x => x.slot === s);
                    return <button key={s} className={`fb-btn fb-btn-sm ${s === slot ? 'fb-btn-primary' : 'fb-btn-secondary'}`}
                        onClick={() => setSlot(s)} onDoubleClick={() => renameSlot(s)} title="double-click to rename">
                        {info?.name || `Plan ${s}`}{info?.exists ? '' : ' ·'}
                    </button>;
                })}
                <button className="fb-btn fb-btn-sm fb-btn-ghost" onClick={() => renameSlot(slot)}>Rename</button>
                {isAdmin && <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--c-text-muted)' }}>
                    Admin — user id: <input value={adminUserId} onChange={e => setAdminUserId(e.target.value.replace(/[^0-9]/g, ''))} placeholder="(me)" style={{ width: 70 }} />
                    <button className="fb-btn fb-btn-sm" style={{ marginLeft: 4 }} onClick={() => { loadSlots(); loadPlan(); }}>Go</button>
                </span>}
            </div>

            <PlanRequestsPanel season={season} onActFor={(id) => { setAdminUserId(id || ''); }} />

            <GroqPanel plan={plan} racesMap={racesMap} />

            {infeasible.length > 0 && (
                <div className="fb-alert fb-alert-warn" style={{ marginTop: 12 }}>
                    ⚠ Plan not feasible on energy for race(s): <b>{infeasible.join(', ')}</b>. Lower CT there, add a Spa/weak race before, or press Recalculate.
                </div>
            )}

            {/* Settings */}
            <div className="fb-card" style={{ marginTop: 12 }}>
                <div className="fb-card-header"><div className="fb-card-title">Goal & presets</div></div>
                <div className="fb-card-body">
                    <div className="fb-row" style={{ flexWrap: 'wrap', gap: 18, alignItems: 'center' }}>
                        <label>Goal <select value={d.goalMode || 'balance'} onChange={e => setField('goalMode', e.target.value)}>
                            <option value="points">Max points</option><option value="money">Max money</option><option value="balance">Balance</option>
                        </select></label>
                        {(d.goalMode || 'balance') === 'balance' && (
                            <label>Money ↔ Points <b>{d.pointsMoneyWeight ?? 50}%</b> pts <input type="range" min="0" max="100" value={d.pointsMoneyWeight ?? 50} onChange={e => setField('pointsMoneyWeight', Number(e.target.value))} /></label>
                        )}
                        <label>Driver level <select value={plan?.driverLevel || 'rookie'} onChange={e => setDriverLevel(e.target.value)}>{DRIVER_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}</select></label>
                        <label><input type="checkbox" checked={!!d.attackSeason} onChange={e => setField('attackSeason', e.target.checked)} /> Attack season</label>
                        <label><input type="checkbox" checked={d.alternateAttackWeak !== false} onChange={e => setField('alternateAttackWeak', e.target.checked)} /> Alternate attack/weak</label>
                        <label><input type="checkbox" checked={d.useSpaForEnergy !== false} onChange={e => setField('useSpaForEnergy', e.target.checked)} /> Use Spa for energy</label>
                        <label>Test sessions (0–17) <input type="number" min="0" max="17" style={{ width: 46 }} value={d.testSessions ?? ""} placeholder="auto" onChange={e => setField("testSessions", e.target.value === "" ? null : Number(e.target.value))} /></label>
                        <label><input type="checkbox" checked={!!d.nextSeasonAdaptation} onChange={e => setField('nextSeasonAdaptation', e.target.checked)} /> Next-season adaptation</label>
                    </div>
                    <div className="fb-row" style={{ flexWrap: 'wrap', gap: 18, marginTop: 10, alignItems: 'center' }}>
                        <label>Driver training preset <select value={d.driverTrainingPreset || ''} onChange={e => setField('driverTrainingPreset', e.target.value || null)}>{DRIVER_TRAININGS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}</select></label>
                        <label>Important CT <input type="number" min="0" max="100" style={{ width: 52 }} value={d.ctImportantMax ?? ''} placeholder="auto" onChange={e => setField('ctImportantMax', e.target.value === '' ? null : Number(e.target.value))} /></label>
                        <label>Medium CT <input type="number" min="0" max="100" style={{ width: 46 }} value={d.ctMedium ?? ""} placeholder="auto" onChange={e => setField("ctMedium", e.target.value === "" ? null : Number(e.target.value))} /></label>
                        <label>Unimportant CT <input type="number" min="0" max="100" style={{ width: 46 }} value={d.ctUnimportant ?? ""} placeholder="auto" onChange={e => setField("ctUnimportant", e.target.value === "" ? null : Number(e.target.value))} /></label>
                        <label>Baseline pos <input type="number" min="1" max="30" style={{ width: 46 }} value={d.baselinePosition ?? 12} onChange={e => setField('baselinePosition', Number(e.target.value))} /></label>
                        <label>Car lvl (during) <input type="number" min="0" max="10" style={{ width: 46 }} value={d.targetCarLevelDuring ?? ''} placeholder="—" onChange={e => setField('targetCarLevelDuring', e.target.value === '' ? null : Number(e.target.value))} /></label>
                        <label>Car lvl (end) <input type="number" min="0" max="10" style={{ width: 46 }} value={d.targetCarLevelEnd ?? ''} placeholder="—" onChange={e => setField('targetCarLevelEnd', e.target.value === '' ? null : Number(e.target.value))} /></label>
                    </div>
                    <div className="fb-row" style={{ marginTop: 12, alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <button className="fb-btn fb-btn-sm fb-btn-primary" onClick={() => saveSettings(draft)} disabled={!settingsDirty}>Apply settings</button>
                        {settingsDirty && <span style={{ color: 'var(--c-warning)', fontSize: 12 }}>unsaved changes</span>}
                        <span style={{ marginLeft: 16, color: 'var(--c-text-muted)', fontSize: 12 }}>Set all races:</span>
                        <button className="fb-btn fb-btn-sm" onClick={() => bulkCategory('important')}>Important</button>
                        <button className="fb-btn fb-btn-sm" onClick={() => bulkCategory('medium')}>Medium</button>
                        <button className="fb-btn fb-btn-sm" onClick={() => bulkCategory('unimportant')}>Unimportant</button>
                        <button className="fb-btn fb-btn-sm fb-btn-ghost" onClick={() => bulkCategory('')}>Auto</button>
                    </div>
                </div>
            </div>

            {/* Actions */}
            <div className="fb-row" style={{ marginTop: 12, alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <button className="fb-btn fb-btn-primary" onClick={() => runOptimize(1)}>Optimize (full season)</button>
                <button className="fb-btn fb-btn-secondary" onClick={() => runOptimize(currentRace)}>Recalculate (from race {currentRace})</button>
                <span style={{ marginLeft: 16, fontSize: 13 }}>Start balance <input value={manualBalance} onChange={e => setManualBalance(e.target.value)} placeholder={fmtMoney(plan?.currentCash)} style={{ width: 120 }} />
                    <button className="fb-btn fb-btn-sm" style={{ marginLeft: 4 }} onClick={applyBalance}>Set</button>
                    <span style={{ color: 'var(--c-text-muted)' }}> ({fmtMoney(startBalance)})</span>
                </span>
                <span style={{ marginLeft: 'auto', fontSize: 14 }}>End balance: <b style={{ color: balAfter[17] < 0 ? 'var(--c-error)' : 'var(--c-success)' }}>{fmtMoney(balAfter[17])}</b></span>
            </div>

            {/* Transposed Excel-like table: rows = fields, cols = races */}
            <div className="fb-table-wrap" style={{ marginTop: 14 }}>
                <table className="fb-table ra-table">
                    <thead>
                        <tr>
                            <th style={{ minWidth: 150 }}>Field</th>
                            {ALL_RACES.map(n => (
                                <th key={n} style={{ textAlign: 'center', background: n === currentRace ? 'var(--c-accent-glow)' : undefined }}>
                                    R{n}<br /><span style={{ fontWeight: 400, color: 'var(--c-text-muted)', fontSize: 11 }}>{trackLabel(n)}</span>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {sections.map((sec, si) => (
                            <React.Fragment key={si}>
                                <tr>
                                    <td colSpan={ALL_RACES.length + 1} style={{ background: '#f3f4f6', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--c-text-muted)' }}>{sec.title}</td>
                                </tr>
                                {sec.rows.map((row, ri) => (
                                    <tr key={ri}>
                                        <td style={{ color: 'var(--c-text-muted)', fontWeight: 500, fontSize: 12, background: '#fafafa' }}>{row.label}</td>
                                        {ALL_RACES.map(n => (
                                            <td key={n} style={{ textAlign: 'center', opacity: n < currentRace ? 0.5 : 1, background: n === currentRace ? 'var(--c-accent-glow)' : undefined }}>
                                                {row.render(n)}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>

            <p style={{ marginTop: 10, fontSize: 12, color: 'var(--c-text-muted)' }}>
                Optimize / Recalculate / edits cost <b>0 GPRO tokens</b> (cached data). Est. position is heuristic — edit &amp; lock (🔒) any cell to pin it; the optimizer won't change locked cells.
            </p>
        </div>
    );
}

// ── Plan requests (holiday-style) ─────────────────────────────

function PlanRequestsPanel({ season, onActFor }) {
    const [open, setOpen] = useState(false);
    const [eligible, setEligible] = useState([]);
    const [pending, setPending] = useState([]);
    const [covered, setCovered] = useState([]);
    const [counterpart, setCounterpart] = useState('');
    const [direction, setDirection] = useState('THEY_PLAN_FOR_ME');

    const reload = useCallback(async () => {
        try {
            const [e, p, c] = await Promise.all([
                api.get('/season-plan-requests/eligible-users').then(r => r.data).catch(() => []),
                api.get('/season-plan-requests/pending').then(r => r.data).catch(() => []),
                api.get('/season-plan-requests/covered').then(r => r.data).catch(() => []),
            ]);
            setEligible(e || []); setPending(p || []); setCovered(c || []);
        } catch { /* ignore */ }
    }, []);
    useEffect(() => { if (open) reload(); }, [open, reload]);

    const uname = (u) => u ? `${u.firstname || ''} ${u.lastname || ''}`.trim() || `#${u.id}` : '';
    const createRequest = async () => {
        if (!counterpart) return;
        try { await api.post('/season-plan-requests', { counterpartUserId: Number(counterpart), direction, season }); setCounterpart(''); reload(); }
        catch (e) { alert(e?.response?.data?.message || 'Failed to create request'); }
    };
    const decide = async (id, action) => { try { await api.post(`/season-plan-requests/${id}/${action}`); reload(); } catch { /* ignore */ } };

    return (
        <div className="fb-card" style={{ marginTop: 12 }}>
            <div className="fb-card-header" style={{ cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>
                <div className="fb-card-title">{open ? '▾' : '▸'} Plan requests &amp; acting-for{pending.length > 0 && <span style={{ color: 'var(--c-warning)' }}> ({pending.length} pending)</span>}</div>
            </div>
            {open && (
                <div className="fb-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {covered.length > 0 && (
                        <div>Act for: <select onChange={e => onActFor(e.target.value)} defaultValue=""><option value="">(myself)</option>{covered.map(u => <option key={u.id} value={u.id}>{uname(u)}</option>)}</select></div>
                    )}
                    <div className="fb-row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <select value={direction} onChange={e => setDirection(e.target.value)}>
                            <option value="THEY_PLAN_FOR_ME">Ask them to plan for me</option>
                            <option value="I_PLAN_FOR_THEM">Offer to plan for them</option>
                        </select>
                        <select value={counterpart} onChange={e => setCounterpart(e.target.value)}>
                            <option value="">— select manager —</option>
                            {eligible.map(u => <option key={u.id} value={u.id}>{uname(u)}</option>)}
                        </select>
                        <button className="fb-btn fb-btn-sm fb-btn-primary" onClick={createRequest}>Send request</button>
                        <span style={{ color: 'var(--c-text-muted)', fontSize: 12 }}>(season {season})</span>
                    </div>
                    {pending.length > 0 && (
                        <div>
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>Pending:</div>
                            {pending.map(r => (
                                <div key={r.id} style={{ marginBottom: 4, fontSize: 13 }}>
                                    {uname(r.planner)} → plans for {uname(r.requester)} (S{r.season}){' '}
                                    <button className="fb-btn fb-btn-sm fb-btn-primary" onClick={() => decide(r.id, 'approve')}>Approve</button>{' '}
                                    <button className="fb-btn fb-btn-sm fb-btn-secondary" onClick={() => decide(r.id, 'reject')}>Reject</button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ── Groq (AI) panel ────────────────────────────────────────────
function GroqPanel({ plan, racesMap }) {
    const [status, setStatus] = useState(null);
    const [token, setToken] = useState('');
    const [out, setOut] = useState(null);
    const [busy, setBusy] = useState(false);
    const reload = useCallback(() => { api.get('/groq/status').then(r => setStatus(r.data)).catch(() => setStatus(null)); }, []);
    useEffect(() => { reload(); }, [reload]);
    if (!status || !status.roleEnabled) return null; // hidden unless admin enabled your role

    const saveToken = async () => { try { await api.post('/groq/token', { token }); setToken(''); reload(); } catch { /* ignore */ } };
    const summary = () => {
        const lines = [`Season ${plan?.season}, driver ${plan?.driverLevel}, goal ${plan?.settings?.goalMode}.`];
        const start = plan?.effectiveStartingBalance ?? 0; let run = start;
        (plan?.races || []).forEach(r => {
            run += (r.racePnl ?? 0);
            const rep = Object.keys(r.parts || {}).length;
            lines.push(`R${r.raceNumber} ${(plan.calendar || []).find(c => c.raceNumber === r.raceNumber)?.trackName || ''}: cat ${r.category || 'auto'}, CT ${r.ctRisk ?? '-'}, estPos ${r.estimatedPosition ?? '-'}, maxWear ${r.maxPartWear ?? '-'}%, repairs ${rep}, balAfter ${Math.round(run / 1000)}k${r.energyInfeasible ? ' ENERGY-INFEASIBLE' : ''}`);
        });
        return lines.join('\n');
    };
    const explain = async () => {
        setBusy(true); setOut(null);
        try { const r = await api.post('/groq/explain', { prompt: summary() }); setOut(r.data?.text || ''); }
        catch (e) { setOut(e?.response?.data?.error || 'AI call failed'); }
        finally { setBusy(false); }
    };
    return (
        <div className="fb-card" style={{ marginTop: 12 }}>
            <div className="fb-card-header"><div className="fb-card-title">AI assistant (Groq)</div></div>
            <div className="fb-card-body">
                {!status.hasToken && (
                    <div className="fb-row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 13, color: 'var(--c-text-muted)' }}>Your Groq token (gsk_…):</span>
                        <input type="password" value={token} onChange={e => setToken(e.target.value)} style={{ width: 240 }} placeholder="gsk_..." />
                        <button className="fb-btn fb-btn-sm fb-btn-primary" onClick={saveToken} disabled={!token}>Save token</button>
                    </div>
                )}
                {status.available && (
                    <div style={{ marginTop: status.hasToken ? 0 : 8 }}>
                        <button className="fb-btn fb-btn-sm fb-btn-primary" onClick={explain} disabled={busy}>{busy ? 'Thinking…' : 'Explain plan (AI)'}</button>
                        {status.hasToken && <button className="fb-btn fb-btn-sm fb-btn-ghost" style={{ marginLeft: 8 }} onClick={() => { setToken(''); api.post('/groq/token', { token: '' }).then(reload); }}>Clear token</button>}
                        {out && <div style={{ marginTop: 10, whiteSpace: 'pre-wrap', fontSize: 13, background: '#f9fafb', border: '1px solid var(--c-border)', borderRadius: 6, padding: 10 }}>{out}</div>}
                    </div>
                )}
            </div>
        </div>
    );
}

// ── small view helpers ─────────────────────────────────────────

function catStyle(cat) {
    const map = {
        important: { background: 'rgba(220,38,38,0.10)', color: '#b23b3b' },
        medium: { background: 'rgba(217,119,6,0.12)', color: '#9a7d2e' },
        unimportant: { background: 'rgba(37,99,235,0.10)', color: '#3b6b7a' },
    };
    return map[cat] || {};
}
function eColor(e) {
    if (e == null) return 'var(--c-text-muted)';
    if (e < 15) return 'var(--c-error)';
    if (e < 40) return 'var(--c-warning)';
    return 'var(--c-success)';
}
function fmt1(v) { return v == null ? '—' : (Math.round(v * 10) / 10); }
function shares(p, h, a) {
    if (p == null || h == null || a == null) return null;
    const s = Math.abs(p) + Math.abs(h) + Math.abs(a);
    if (s <= 0) return null;
    return [Math.round(Math.abs(p) / s * 100), Math.round(Math.abs(h) / s * 100), Math.round(Math.abs(a) / s * 100)];
}
function coloredPha(p, h, a, ascending) {
    const sh = shares(p, h, a);
    if (!sh) return '—';
    const col = phaColors(p, h, a, ascending);
    return <span><span style={{ color: col.p }}>{sh[0]}</span> / <span style={{ color: col.h }}>{sh[1]}</span> / <span style={{ color: col.a }}>{sh[2]}</span></span>;
}
function matchPct(ts, cs) {
    const l1 = Math.abs(ts[0] - cs[0]) + Math.abs(ts[1] - cs[1]) + Math.abs(ts[2] - cs[2]); // 0..200
    return Math.max(0, Math.round(100 - l1 / 2));
}
